import { useSelector } from "react-redux";
import { getActiveStore, getUser } from "src/redux/selectors";
import { useContext, useMemo } from "react";
import { OnboardingContext } from "#onboarding/utils/OnboardingContext";
import { Routes } from "#navigation/routes";
import { usePromotions } from "#promotion/hooks/usePromotions";
import { PromotionType } from "src/api/graphql/generated/types";
import {
    getCateringSetupUrl,
    getFirstPartyDeliverySetupUrl,
    getMenuSetupUrl,
    getThirdPartyDeliverySetupUrl,
    getWebsiteSetupUrl,
} from "#shared-components/setup-form-urls";
import api from "src/api/rest";
import { t } from "@lingui/core/macro";

type OnboardingSection = {
    title: string;
    route?: string;
    fields: {
        label: string;
        value: string | undefined | null;
        required: boolean;
        setupAction?: {
            label: string;
            url?: string;
            onClick?: () => void;
        };
    }[];
};

export const useStoreOnboardingStatus = () => {
    const store = useSelector(getActiveStore);
    const user = useSelector(getUser);
    const { verification } = useContext(OnboardingContext);
    const { promotions } = usePromotions();

    const hasActivePromotionOfType = useMemo(
        () => (type: string) =>
            promotions?.some(
                (promo) =>
                    promo.type === type && promo.isRunning && !promo.isArchived,
            ),
        [promotions],
    );

    // Add later once we clean up promotions
    // const hasActiveSignUpPromotion = promotions?.some(
    //     (promo) =>
    //         promo.conditions?.pickupOnly &&
    //         promo.isRunning &&
    //         !promo.isArchived,
    // );

    const sections = useMemo<OnboardingSection[]>(
        () => [
            {
                title: t`Business Information`,
                route: Routes.SettingsBusinessInfo,
                fields: [
                    {
                        label: t`Store Name`,
                        value: store?.name,
                        required: true,
                    },
                    { label: t`Email`, value: store?.email, required: true },
                    {
                        label: t`Phone`,
                        value: store?.phoneNumber,
                        required: true,
                    },
                    {
                        label: t`Address`,
                        value: store?.addressComponents?.full,
                        required: true,
                    },
                    {
                        label: t`Timezone`,
                        value: store?.hours?.zone,
                        required: true,
                    },
                    {
                        label: t`Store Type`,
                        value: store?.kind,
                        required: true,
                    },
                    {
                        label: t`Store Hours`,
                        value:
                            store?.hours?.local && store.hours.local.length > 0
                                ? t`Set`
                                : undefined,
                        required: true,
                    },
                ],
            },
            {
                title: t`Business Verification`,
                route: Routes.Payouts,
                fields: [
                    {
                        label: t`Legal Business Name`,
                        value: verification?.name,
                        required: true,
                    },
                    {
                        label: t`Employer Identification Number (EIN)`,
                        value: verification?.taxId ? t`Provided` : undefined,
                        required: true,
                    },
                    {
                        label: t`Business Phone Number`,
                        value: verification?.phone,
                        required: true,
                    },
                    {
                        label: t`Bank Account`,
                        value: verification?.accountLast4
                            ? t`Connected`
                            : undefined,
                        required: true,
                    },
                ],
            },
            {
                title: t`Brand and Appearance`,
                route: Routes.SettingsBrandAndAppearance,
                fields: [
                    { label: t`Username`, value: store?.slug, required: true },
                    { label: t`Logo`, value: store?.logoUrl, required: true },
                    {
                        label: t`Cover Photo`,
                        value: store?.thumbnailUrl,
                        required: true,
                    },
                    {
                        label: t`Brand Color`,
                        value: store?.brandColor,
                        required: true,
                    },
                    {
                        label: t`Store Emoji`,
                        value: store?.emoji === "🍽️" ? undefined : store?.emoji,
                        required: false,
                    },
                    {
                        label: t`Kiosk Splash Video`,
                        value: store?.kioskPreferences?.attractScreenUrl,
                        required: false,
                    },
                    {
                        label: t`Digital Receipt Background`,
                        value: store?.loyaltyCardBackgroundImage,
                        required: false,
                    },
                    {
                        label: t`Register Secondary Display`,
                        value: store?.brandSplashUrl,
                        required: false,
                    },
                ],
            },
            {
                title: t`Tax Settings`,
                route: Routes.SettingsTax,
                fields: [
                    {
                        label: t`Tax Rate`,
                        value: store?.taxRate ? `${store.taxRate}%` : undefined,
                        required: true,
                    },
                ],
            },
            {
                title: t`Menu`,
                fields: [
                    {
                        label: t`Set up Menu`,
                        value:
                            (store?.productCategories ?? []).length > 0
                                ? t`Set`
                                : undefined,
                        required: false,
                        setupAction:
                            (store?.productCategories ?? []).length > 0
                                ? undefined
                                : {
                                      label: t`Set Up`,
                                      url: getMenuSetupUrl({ store }),
                                  },
                    },
                ],
            },
            {
                title: t`All Orders`,
                route: Routes.SettingsAllOrders,
                fields: [
                    {
                        label: t`Bag Fee`,
                        value: t`Set`,
                        required: false,
                    },
                    {
                        label: t`Google Review URL`,
                        value: store?.externalReviewUrl,
                        required: false,
                    },
                ],
            },
            {
                title: t`Tipping`,
                route: Routes.SettingsTipping,
                fields: [
                    {
                        label: t`Tip Image`,
                        value: store?.tipImageUrl,
                        required: false,
                    },
                ],
            },
            {
                title: t`Promotions`,
                route: Routes.Promotion,
                fields: [
                    {
                        label: t`Rewards`,
                        value: hasActivePromotionOfType(PromotionType.Reward)
                            ? t`Enabled`
                            : undefined,
                        required: false,
                    },
                ],
            },
            {
                title: t`Gift Cards`,
                route: Routes.SettingsGiftCard,
                fields: [
                    {
                        label: t`Digital Gift Cards`,
                        value: store?.digitalGiftCardAvailable
                            ? t`Enabled`
                            : undefined,
                        required: false,
                    },
                    {
                        label: t`Physical Gift Cards`,
                        value: store?.physicalGiftCardAvailable
                            ? t`Enabled`
                            : undefined,
                        required: false,
                    },
                ],
            },
            {
                title: t`Delivery`,
                fields: [
                    {
                        label: t`First Party Delivery`,
                        value: store?.delivery && store?.storeDelivers ? t`Set` : undefined,
                        required: false,
                        setupAction: {
                            label: t`Set Up`,
                            url: getFirstPartyDeliverySetupUrl({ store, user }),
                        },
                    },
                    {
                        label: t`Third Party Delivery Integration`,
                        value: store?.posIntegrations?.deliverect?.enabled
                            ? t`Set`
                            : undefined,
                        required: false,
                        setupAction: {
                            label: t`Set Up`,
                            url: getThirdPartyDeliverySetupUrl({ store, user }),
                        },
                    },
                ],
            },
            {
                title: t`Other Products`,
                fields: [
                    {
                        label: t`Custom Website`,
                        value: store?.websiteEnabled ? t`Set` : undefined,
                        required: false,
                        setupAction: {
                            label: t`Set Up`,
                            url: getWebsiteSetupUrl({ store, user }),
                        },
                    },

                    {
                        label: t`Catering`,
                        value: store?.cateringEnabled ? t`Set` : undefined,
                        required: false,
                        setupAction: {
                            label: t`Set Up`,
                            url: getCateringSetupUrl({ store }),
                        },
                    },
                    {
                        label: t`Snackpass App`,
                        value: store?.snackpassAppEnabled ? t`Set` : undefined,
                        required: false,
                        setupAction: {
                            label: t`Set Up`,
                            url: Routes.SettingsProducts,
                        },
                    },
                ],
            },
        ],
        [store, verification, hasActivePromotionOfType, user],
    );

    const getMissingFields = useMemo(() => {
        const missing: { section: string; fields: string[] }[] = [];

        sections.forEach((section) => {
            const missingInSection = section.fields
                .filter((field) => field.required && !field.value)
                .map((field) => field.label);

            if (missingInSection.length > 0) {
                missing.push({
                    section: section.title,
                    fields: missingInSection,
                });
            }
        });

        return missing;
    }, [sections]);

    const isFullyOnboarded = useMemo(
        () =>
            sections.every((section) =>
                section.fields.every(
                    (field) => !field.required || Boolean(field.value),
                ),
            ),
        [sections],
    );

    const onboardingComplete = store?.onboardingComplete;

    const markOnboardingAsComplete = async () => {
        if (!store?._id) return;

        // Redudant but just in case
        if (store?.onboardingComplete) {
            return;
        }

        try {
            await api.stores.finishOnboarding(store._id);
        } catch (error) {
            console.error("Failed to mark onboarding as complete:", error);
        }
    };

    return {
        isFullyOnboarded,
        missingFields: getMissingFields,
        sections,
        onboardingComplete,
        markOnboardingAsComplete,
    };
};
